/** DESENUL UNEI COTE: linia întreruptă de cifră, cu buline la capete.
 *
 *  Așezarea o face `cote.ts`; aici e doar tușul. Toate mărimile sunt în
 *  PIXELI împărțiți la mărire, ca linia să aibă mereu aceeași grosime și cifra
 *  același corp, oricât te-ai apropia — o cotă e o adnotare, nu geometrie.
 *
 *  CIFRA STĂ DREAPTĂ: pe o latură verticală textul se rotește cu 90°, dar
 *  niciodată cu capul în jos — o cotă de la 91° la 269° se întoarce, cum
 *  face orice CAD.
 *
 *  ȘI STĂ LA MIJLOC, afară de când așezarea i-a găsit alt loc pe linia ei
 *  (`Cota::la`) fiindcă la mijloc ar fi căzut peste ceva. Linia se rupe acolo
 *  unde e cifra, oriunde ar fi ea.
 */
import { numar } from "../../i18n";
import { COTA_PX, latimeaCifrei, punctulCifrei, type Cota } from "./cote";

/** Tușul cotelor de planșă: negru, ca al zidului. */
export const COTA_TUS = "#141414";

/** TĂLPILE OBLICE ale cotei, când nu sunt buline: jumătate de lungime, în
 *  pixeli, și unghiul față de linia de cotă. Convenția planșelor de
 *  arhitectură (liniuța la 60° a planșelor de referință ArchiCAD); pe ecran
 *  rămân bulinele, care se citesc mai bine la mărime mică. */
export const TALPA_PX = { jumatate: 2.2, unghi: 60 };

export function CotaDesen({ c, k, culoare = COTA_TUS, corp: corpCerut,
                            hartie = "#fff", capete = "buline" }: {
  c: Cota; k: number; culoare?: string;
  /** Capetele liniei: buline (ecranul) sau tălpi oblice (planșa DWG). */
  capete?: "buline" | "talpi";
  /** Pernuța de sub cifră, când linia n-are loc de gol: culoarea hârtiei.
   *  Pe planșa de noapte a nivelului hârtia e neagră. */
  hartie?: string;
  /** Corpul cifrei, în pixeli, pentru toate cotele desenate deodată. Cotele de
   *  lucru îl au un pic mai mare. Cel scris PE cotă (`Cota::corp` — cifra unei
   *  uși) bate și cererea asta: el e o însușire a măsurii, nu a desenului. */
  corp?: number;
}) {
  const corp = c.corp ?? corpCerut ?? COTA_PX.corp;
  const dx = c.b[0] - c.a[0];
  const dy = c.b[1] - c.a[1];
  const L = Math.hypot(dx, dy);
  if (!(L > 0) || !(k > 0)) return null;
  const ux = dx / L;
  const uy = dy / L;
  const t = c.la ?? 0.5;
  // The line breaks only where the figure really stands on it (`Cota::deplasare`).
  const lx = c.a[0] + dx * t;
  const ly = c.a[1] + dy * t;
  const [mx, my] = punctulCifrei(c);
  const cifra = (v: number) => numar(v, { minimumFractionDigits: 2,
                                          maximumFractionDigits: 2 });
  // «1,03/2,10» — v. `Cota::si`. Bara fără spații, ca pe planșă.
  const text = c.si != null ? `${cifra(c.valoare)}/${cifra(c.si)}`
                            : cifra(c.valoare);
  // Jumătatea golului din linie: cifra plus perna, în metri.
  const gol = (latimeaCifrei(text, corp) / 2 + COTA_PX.perna) / k;
  const cuGol = !c.deplasare && Math.min(t, 1 - t) * L > gol + (2 * COTA_PX.bulina) / k;
  let unghi = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (unghi > 90 || unghi <= -90) unghi += 180;
  const r = COTA_PX.bulina / k;
  return (
    <g pointerEvents="none">
      {cuGol ? (
        <>
          <line x1={c.a[0]} y1={c.a[1]} x2={lx - ux * gol} y2={ly - uy * gol}
                stroke={culoare} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
          <line x1={lx + ux * gol} y1={ly + uy * gol} x2={c.b[0]} y2={c.b[1]}
                stroke={culoare} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
        </>
      ) : (
        <line x1={c.a[0]} y1={c.a[1]} x2={c.b[0]} y2={c.b[1]}
              stroke={culoare} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      )}
      {capete === "talpi" ? [c.a, c.b].map((p, i) => {
        const a = ((unghi + TALPA_PX.unghi) * Math.PI) / 180;
        const l = TALPA_PX.jumatate / k;
        return (
          <line key={i} x1={p[0] - Math.cos(a) * l} y1={p[1] - Math.sin(a) * l}
                x2={p[0] + Math.cos(a) * l} y2={p[1] + Math.sin(a) * l}
                stroke={culoare} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
        );
      }) : (
        <>
          <circle cx={c.a[0]} cy={c.a[1]} r={r} fill={culoare} />
          <circle cx={c.b[0]} cy={c.b[1]} r={r} fill={culoare} />
        </>
      )}
      <text x={mx} y={my} textAnchor="middle" dominantBaseline="central"
            fontSize={corp / k} fontWeight={500} fill={culoare}
            fontFamily="ui-sans-serif, system-ui, sans-serif"
            transform={`rotate(${unghi} ${mx} ${my})`}
            // Când linia nu are loc de gol, cifra stă pe o pernuță de hârtie.
            {...(cuGol ? {} : { stroke: hartie, strokeWidth: (corp * 0.3) / k,
                                paintOrder: "stroke" as const })}>
        {text}
      </text>
    </g>
  );
}

/** Mai multe cote, dintr-o listă gata așezată. */
export function Cote({ cote, k, culoare, corp, hartie, capete }: {
  cote: readonly Cota[]; k: number; culoare?: string; corp?: number; hartie?: string;
  capete?: "buline" | "talpi";
}) {
  return (
    <g className="ob-cote" pointerEvents="none">
      {cote.map((c, i) => (
        <CotaDesen key={i} c={c} k={k} culoare={culoare} corp={corp} hartie={hartie}
                   capete={capete} />
      ))}
    </g>
  );
}
